import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { CROption } from '@smart-builder/types';
import { APIParams, APIResult, callAPI } from '../../callApi';
import { createAsyncActionResource } from '../util';
import {
  ApiActionType,
  BodyState,
  EndPointBuilder,
  ItemSummaryParser,
  KeyComparer,
  LoadMultiPayload,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
} from './types';
import { createErrorHandler } from './util';

type SuccessPayload<TItemSummary> = {
  frameMultiSeqNumber: number;
  results: TItemSummary[];
  populateOptions?: Record<string, CROption[]>;
};

export function createLoadMultiAction<
  TItemKey,
  TItemSummary extends TItemKey,
  TItemBody extends TItemKey,
  TItemSummaryAPI,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  parseItemSummary: ItemSummaryParser<TItemSummary, TItemSummaryAPI>,
  loadAsync: any,
) {
  const loadMultiAsync = createAsyncActionResource<
    LoadMultiPayload,
    SuccessPayload<TItemSummary>
  >(displayName, 'loadMulti');

  function doFetch(params: APIParams): Promise<APIResult> {
    return callAPI(getEndpoint(), 'GET', params);
  }

  function* loadMultiRequestSaga(
    action: ActionType<typeof loadMultiAsync.request>,
  ): Generator {
    try {
      const params = action.payload;

      const { frameMultiSeqNumber } = (yield select(
        selector,
      )) as MainResourceState<TItemKey, TItemSummary, TItemBody>;
      const { results, populateOptions } = yield* call(doFetch, params);

      const parsedResults = results!.map(parseItemSummary);
      yield put(
        loadMultiAsync.success({
          results: parsedResults,
          populateOptions,
          frameMultiSeqNumber,
        }),
      );
    } catch (error) {
      yield put(loadMultiAsync.failure(error));
    }
  }

  const loadMultiRequestHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof loadMultiAsync.request
  > = (state, action) => {
    return {
      ...state,
      lastLoadMultiParams: action.payload,
      // If multiple loadMultis are triggered in short succession,
      // only the last one will have it's response taken.
      frameMultiSeqNumber: state.frameMultiSeqNumber + 1,
      // If a loadSingle is triggered prior to loadMulti, we want to
      // ignore the result of the loadSingle, as the loadMulti should
      // be triggering another loadSingle
      frameSeqNumber: state.frameSeqNumber + 1,
      pendingLoads: state.pendingLoads + 1,
      error: undefined,
      patchData: undefined,
    };
  };

  function* loadMultiSuccessSaga(
    action: ActionType<typeof loadMultiAsync.success>,
  ): Generator {
    const { currentRecordIndex, currentRecord, lastLoadParams } = (yield select(
      selector,
    )) as MainResourceState<TItemKey, TItemSummary, TItemBody>;
    if (currentRecordIndex !== undefined) {
      yield put(
        loadAsync(
          lastLoadParams ?? {
            key: currentRecord!.summary,
          },
        ),
      );
    }
  }

  const loadMultiSuccessHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof loadMultiAsync.success
  > = (state, action) => {
    const { results, populateOptions, frameMultiSeqNumber } = action.payload;

    if (state.frameMultiSeqNumber > frameMultiSeqNumber) {
      // ignore this load, a newer load has been requested
      return {
        ...state,
        pendingLoads: state.pendingLoads - 1,
        patchData: undefined,
      };
    }

    const records = results.map(item => ({
      bodyState: BodyState.browse,
      summary: item,
      body: undefined,

      loadedLockNum: (item as Record<any, any>)['lockNum'],
      seqNumCur: undefined,
      seqNumFrom: undefined,

      pendingChanges: [],
      validation: undefined,
    }));

    const _newRecordIndex = state.currentRecord
      ? records.findIndex(r => sameKey(r.summary, state.currentRecord!.summary))
      : -1;
    const hasNewRecord = _newRecordIndex >= 0;
    const newRecordIndex = hasNewRecord ? _newRecordIndex : undefined;

    return {
      ...state,
      pendingLoads: state.pendingLoads - 1,

      records,

      insertedKeys: [],
      deletedKeys: [],

      currentRecordIndex: newRecordIndex,
      currentRecord: hasNewRecord ? records[newRecordIndex!] : undefined,
      currentBody: hasNewRecord ? state.currentBody : undefined,
      currentItems: records.map(r => r.summary),

      lastLoadParams: hasNewRecord ? state.lastLoadParams : undefined,
      patchData: undefined,
      populateOptions,
      lastRecordsChangedAction: ApiActionType.loadMulti,

      hasLoadMulti: true,
    };
  };

  const loadMultiFailureHandler = createErrorHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof loadMultiAsync.failure
  >(false);

  return {
    loadMultiAsync,
    loadMultiRequestSaga,
    loadMultiRequestHandler,
    loadMultiSuccessSaga,
    loadMultiSuccessHandler,
    loadMultiFailureHandler,
  };
}
